import { BackendApiClientType } from "@/manager/backend-api-client/types";
import { BackendApiErrorKind } from "@/modules/api/types";
import { validationModule } from "@/modules/validation";

/**
 * A basic type for functions that need to work with an API response object.
 */
export type BackendApiResponse = {
  data?: unknown;
  error?: unknown;
  response?: Awaited<ReturnType<BackendApiClientType["GET"]>>["response"];
};

export const responseMatchesBackendApiErrorKind = ({
  response,
  errorKind,
}: {
  response: BackendApiResponse;
  errorKind: BackendApiErrorKind;
}) => {
  if (!validationModule.objectHasKey(response, "error")) {
    return false;
  }

  const errorInfo = response.error as unknown;

  if (!validationModule.objectHasKey(errorInfo, "error_metadata")) {
    return false;
  }

  const errorMetadata = errorInfo.error_metadata;

  if (!validationModule.objectHasKey(errorMetadata, "error_kind")) {
    return false;
  }

  return errorMetadata.error_kind === errorKind;
};

export const getBackendApiErrorMessage = (error: unknown): string | undefined => {
  if (validationModule.objectHasKey(error, "error_metadata")) {
    const errorMetadata = error.error_metadata;
    if (
      validationModule.objectHasKey(errorMetadata, "message") &&
      typeof errorMetadata.message === "string"
    ) {
      return errorMetadata.message;
    }
  }

  if (
    validationModule.objectHasKey(error, "error_message") &&
    typeof error.error_message === "string"
  ) {
    return error.error_message;
  }

  if (validationModule.objectHasKey(error, "detail") && typeof error.detail === "string") {
    return error.detail;
  }

  return undefined;
};

export const isAbortError = (error: unknown): boolean => {
  if (typeof error !== "object" || error === null) {
    return false;
  }
  return "name" in error && error.name === "AbortError";
};
